import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalBody, ModalFooter, useToast } from '../sectepe-ui/index';
import { generationApi, GenerateTextRequest, scheduledPostApi, templateApi, Template } from '../services/api';
import { PageHeader } from '../components/ui';
import { Icon } from '../components/icons';
import { fehlerText } from '../vertrieb/format';
import { variablenAus, variablenLabel } from '../inhalte/workflowSchritte';
import { TEMPLATE_KATEGORIEN, kategorieLabel } from '../inhalte/templateKategorien';

type Anbieter = GenerateTextRequest['provider'];
type Ziel = 'linkedin' | 'email' | 'blog';

const ANBIETER: { id: Anbieter; label: string }[] = [
  { id: 'openai', label: 'OpenAI' },
  { id: 'claude', label: 'Claude' },
  { id: 'perplexity', label: 'Perplexity (mit Websuche)' },
];

const ARTEN: Record<string, string> = { ad: 'Anzeige', text: 'Allgemeiner Text', content: 'Inhalt' };

const ZIELE: { id: Ziel; label: string }[] = [
  { id: 'linkedin', label: 'LinkedIn-Beitrag' },
  { id: 'email', label: 'E-Mail' },
  { id: 'blog', label: 'Blogartikel' },
];

/** Fruehester Termin fuer "Einplanen": sieben Tage, weil vor dem Posten freigegeben wird. */
const fruehesterTermin = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  d.setSeconds(0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

/**
 * Freier Text-Generator. Kommt ein Template ueber den Navigationszustand
 * (Template-Bibliothek), werden seine Platzhalter als Felder abgefragt.
 * Ein eigener Prompt mit {{platzhaltern}} laesst sich als Template speichern.
 */
const GenerateTextPage: React.FC = () => {
  const location = useLocation();
  const { push } = useToast();
  const [anbieter, setAnbieter] = useState<Anbieter>('openai');
  const [prompt, setPrompt] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [art, setArt] = useState('text');
  const [ergebnis, setErgebnis] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const [template, setTemplate] = useState<Template | null>(null);
  const [variablen, setVariablen] = useState<Record<string, string>>({});

  const [ziel, setZiel] = useState<Ziel>('linkedin');
  const [termin, setTermin] = useState(fruehesterTermin());
  const [plant, setPlant] = useState(false);
  const [eingeplant, setEingeplant] = useState(false);
  // ID des Freigabe-Eintrags, damit "Zur Freigabe" genau dort landet.
  const [geplanteId, setGeplanteId] = useState<string | null>(null);

  const [zeigeSpeichern, setZeigeSpeichern] = useState(false);
  const [vorlage, setVorlage] = useState({ name: '', kategorie: 'text', beschreibung: '', tags: '' });
  const [speichert, setSpeichert] = useState(false);

  useEffect(() => {
    const t = (location.state as { template?: Template } | null)?.template;
    if (!t) return;
    setTemplate(t);
    setSystemPrompt(t.systemPrompt || '');
    setArt(t.category);
    setVariablen(Object.fromEntries(t.variables.map((v) => [v.name, v.example || ''])));
  }, [location.state]);

  // Prompt aus dem Template mit den Variablen fuellen.
  useEffect(() => {
    if (!template) return;
    let gefuellt = template.prompt;
    Object.entries(variablen).forEach(([k, v]) => {
      gefuellt = gefuellt.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v);
    });
    setPrompt(gefuellt);
  }, [template, variablen]);

  const platzhalter = useMemo(() => (template ? [] : variablenAus([prompt, systemPrompt])), [template, prompt, systemPrompt]);

  const templateLoesen = () => {
    setTemplate(null);
    setPrompt('');
    setSystemPrompt('');
    setVariablen({});
    setArt('text');
  };

  const erzeugen = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    setLaeuft(true);
    setFehler(null);
    setErgebnis(null);
    setEingeplant(false);
    try {
      setErgebnis(await generationApi.generateText({ provider: anbieter, prompt, systemPrompt: systemPrompt || undefined, type: art }));
      setTermin(fruehesterTermin());
    } catch (err) {
      setFehler(fehlerText(err, 'Die Erzeugung ist fehlgeschlagen'));
    } finally {
      setLaeuft(false);
    }
  };

  const einplanen = async () => {
    if (!ergebnis?.id || !termin) return;
    setPlant(true);
    setFehler(null);
    try {
      const geplant = await scheduledPostApi.schedulePost({ generatedContentId: ergebnis.id, scheduledDate: new Date(termin).toISOString(), contentType: ziel });
      setGeplanteId(geplant?.id ?? null);
      setEingeplant(true);
      push({ message: 'Eingeplant. Vor dem Veröffentlichen wird freigegeben.', severity: 'success' });
    } catch (err) {
      setFehler(fehlerText(err, 'Einplanen fehlgeschlagen'));
    } finally {
      setPlant(false);
    }
  };

  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(ergebnis?.content || '');
      push({ message: 'In die Zwischenablage kopiert.', severity: 'success' });
    } catch {
      push({ message: 'Kopieren nicht möglich – bitte markieren und manuell kopieren.', severity: 'warning' });
    }
  };

  const alsTemplateSpeichern = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vorlage.name.trim() || !prompt.trim()) return;
    setSpeichert(true);
    try {
      await templateApi.createTemplate({
        name: vorlage.name.trim(),
        description: vorlage.beschreibung.trim() || undefined,
        category: vorlage.kategorie,
        prompt,
        systemPrompt: systemPrompt || undefined,
        variables: platzhalter.map((name) => ({ name })),
        tags: vorlage.tags.split(',').map((t) => t.trim()).filter(Boolean),
      });
      setZeigeSpeichern(false);
      push({ message: `Template „${vorlage.name.trim()}“ gespeichert.`, severity: 'success' });
    } catch (err) {
      setFehler(fehlerText(err, 'Template konnte nicht gespeichert werden'));
      setZeigeSpeichern(false);
    } finally {
      setSpeichert(false);
    }
  };

  const artOptionen = { ...ARTEN, ...(ARTEN[art] ? {} : { [art]: kategorieLabel(art) }) };

  return (
    <div className="page-container">
      <PageHeader
        eyebrow="Inhalte & Werkzeuge"
        title="Text generieren"
        subtitle="Ein Prompt, ein Ergebnis. Für wiederkehrende Texte lohnt eine Vorlage, für mehrstufige Abläufe ein Workflow."
        actions={<Link to="/templates" className="btn btn-secondary"><Icon name="template" size={14} /> Vorlagen</Link>}
      />

      {template && (
        <div className="flow-banner flow-banner--info" role="status">
          <Icon name="template" size={16} />
          <span>Template <strong>{template.name}</strong>{template.description ? ` – ${template.description}` : ''}</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={templateLoesen}>Template lösen</button>
        </div>
      )}

      <form className="card inhalt-formular" onSubmit={erzeugen} aria-label="Text generieren">
        {template && template.variables.length > 0 && (
          <>
            <h2 className="card-title">Platzhalter</h2>
            <div className="inhalt-felder">
              {template.variables.map((v) => (
                <label key={v.name}>
                  {v.description || variablenLabel(v.name)}
                  <input
                    type="text"
                    value={variablen[v.name] ?? ''}
                    placeholder={v.example || v.name}
                    onChange={(e) => setVariablen((alt) => ({ ...alt, [v.name]: e.target.value }))}
                  />
                </label>
              ))}
            </div>
          </>
        )}

        <div className="inhalt-felder">
          <label>
            Anbieter
            <select value={anbieter} onChange={(e) => setAnbieter(e.target.value as Anbieter)}>
              {ANBIETER.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </label>
          <label>
            Art
            <select value={art} onChange={(e) => setArt(e.target.value)} disabled={Boolean(template)}>
              {Object.entries(artOptionen).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </label>
          <label className="inhalt-felder__breit">
            Prompt *
            <textarea rows={7} required value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Was soll entstehen? Platzhalter wie {{thema}} werden beim Speichern als Template zu Feldern." readOnly={Boolean(template)} />
          </label>
        </div>

        <details className="inhalt-details" open={Boolean(systemPrompt)}>
          <summary>System-Prompt (optional)</summary>
          <textarea className="form-textarea" rows={3} value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)} placeholder="z. B. Du schreibst sachlich, auf Deutsch, ohne Floskeln." aria-label="System-Prompt" />
        </details>

        {!template && platzhalter.length > 0 && (
          <p className="text-xs text-secondary">Platzhalter im Prompt: {platzhalter.map((p) => <code key={p}>{`{{${p}}}`} </code>)}</p>
        )}

        <div className="btn-group">
          <button type="submit" className="btn btn-primary" disabled={laeuft || !prompt.trim()}>
            <Icon name="sparkles" size={14} /> {laeuft ? 'Erzeugt…' : 'Generieren'}
          </button>
          {!template && (
            <button type="button" className="btn btn-secondary" onClick={() => setZeigeSpeichern(true)} disabled={!prompt.trim()}>
              <Icon name="template" size={14} /> Als Template speichern
            </button>
          )}
        </div>
      </form>

      {fehler && <div className="alert alert-error">{fehler}</div>}

      {ergebnis && (
        <section className="card" aria-label="Ergebnis">
          <div className="inhalt-ergebnis__kopf">
            <h2 className="card-title">Ergebnis</h2>
            <div className="btn-group">
              <button type="button" className="btn btn-secondary btn-sm" onClick={kopieren}><Icon name="copy" size={14} /> Kopieren</button>
            </div>
          </div>
          <div className="result-container"><ReactMarkdown remarkPlugins={[remarkGfm]}>{ergebnis.content || ''}</ReactMarkdown></div>
          <p className="text-xs text-secondary">
            {ergebnis.provider}{ergebnis.model && <> · {ergebnis.model}</>}{ergebnis.tokensUsed && <> · {ergebnis.tokensUsed} Tokens</>}
          </p>

          <div className="inhalt-einplanen" aria-label="Einplanen">
            <h3>Einplanen</h3>
            {eingeplant ? (
              <div className="flow-banner flow-banner--done" role="status">
                <Icon name="check" size={16} />
                <span>Eingeplant für {new Date(termin).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}. Vor dem Veröffentlichen wird freigegeben.</span>
                <Link to={geplanteId ? `/scheduled-posts?id=${encodeURIComponent(geplanteId)}` : '/scheduled-posts'} className="btn btn-primary btn-sm">Zur Freigabe <Icon name="arrow-right" size={14} /></Link>
              </div>
            ) : (
              <div className="quick-form">
                <label>
                  Als
                  <select value={ziel} onChange={(e) => setZiel(e.target.value as Ziel)}>
                    {ZIELE.map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
                  </select>
                </label>
                <label>
                  Termin (frühestens in 7 Tagen)
                  <input type="datetime-local" value={termin} min={fruehesterTermin()} onChange={(e) => setTermin(e.target.value)} />
                </label>
                <div className="quick-form__actions">
                  <button type="button" className="btn btn-primary" onClick={einplanen} disabled={plant || !termin}>
                    <Icon name="calendar" size={14} /> {plant ? 'Plant ein…' : 'Einplanen'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <Modal open={zeigeSpeichern} onOpenChange={(o) => { if (!o) setZeigeSpeichern(false); }}>
        <ModalContent>
          <form onSubmit={alsTemplateSpeichern} aria-label="Als Template speichern">
            <ModalHeader>
              <ModalTitle>Als Template speichern</ModalTitle>
              <ModalDescription>
                {platzhalter.length
                  ? `Die Platzhalter ${platzhalter.map((p) => `{{${p}}}`).join(', ')} werden beim Verwenden als Felder abgefragt.`
                  : 'Tipp: Platzhalter wie {{thema}} im Prompt werden beim Verwenden als Felder abgefragt.'}
              </ModalDescription>
            </ModalHeader>
            <ModalBody>
              <div className="akte-felder">
                <label>Name *<input required autoFocus value={vorlage.name} onChange={(e) => setVorlage({ ...vorlage, name: e.target.value })} /></label>
                <label>
                  Kategorie
                  <select value={vorlage.kategorie} onChange={(e) => setVorlage({ ...vorlage, kategorie: e.target.value })}>
                    {Object.entries(TEMPLATE_KATEGORIEN).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                  </select>
                </label>
                <label>Tags (Komma-getrennt)<input value={vorlage.tags} onChange={(e) => setVorlage({ ...vorlage, tags: e.target.value })} /></label>
              </div>
              <label className="lead-formular__notizen">
                Beschreibung
                <textarea className="form-textarea" rows={2} value={vorlage.beschreibung} onChange={(e) => setVorlage({ ...vorlage, beschreibung: e.target.value })} />
              </label>
            </ModalBody>
            <ModalFooter>
              <button type="button" className="btn btn-ghost" onClick={() => setZeigeSpeichern(false)}>Abbrechen</button>
              <button type="submit" className="btn btn-primary" disabled={speichert || !vorlage.name.trim()}>{speichert ? 'Speichert…' : 'Speichern'}</button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default GenerateTextPage;
